/* =============================================================================
   NIELSEN FABWORX THEME — theme.js
   Vanilla JS, no dependencies, no build step required.
   All components are Custom Elements registered at the bottom.
   ============================================================================= */

(function () {
  'use strict';

  /* ===========================================================================
     PubSub — tiny publish/subscribe event bus for cross-component communication
     =========================================================================== */
  const PubSub = {
    _events: {},
    publish(event, data) {
      if (!this._events[event]) return;
      this._events[event].forEach(fn => fn(data));
    },
    subscribe(event, fn) {
      if (!this._events[event]) this._events[event] = [];
      this._events[event].push(fn);
      return () => this.unsubscribe(event, fn);
    },
    unsubscribe(event, fn) {
      if (!this._events[event]) return;
      this._events[event] = this._events[event].filter(f => f !== fn);
    }
  };

  const EVENTS = {
    cartUpdate:     'cart:update',
    cartDrawerOpen: 'cart:drawer-open',
    cartDrawerClose:'cart:drawer-close',
    cartError:      'cart:error',
    variantChange:  'variant:change',
    searchOpen:     'search:open',
    searchClose:    'search:close',
  };

  /* ===========================================================================
     Utils
     =========================================================================== */
  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    function handleTab(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    container.addEventListener('keydown', handleTab);
    first.focus();
    return () => container.removeEventListener('keydown', handleTab);
  }

  function removeTrapFocus() {
    // Called when drawer closes — focus will naturally return via aria-controls
  }

  function fetchJSON(url, opts = {}) {
    return fetch(url, {
      ...opts,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(opts.headers || {}) }
    }).then(r => {
      if (!r.ok) throw new Error(r.statusText);
      return r.json();
    });
  }

  function moneyFormat(cents) {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);
  }

  function showNotification(message, type = 'success') {
    const el = document.createElement('div');
    el.className = `notification notification--${type}`;
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 4000);
  }

  /* ===========================================================================
     StickyHeader — adds data-scrolled on scroll for CSS to react to
     =========================================================================== */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this._header = this.querySelector('.header');
      if (!this._header) return;

      this._threshold = 10;
      this._onScroll = this._handleScroll.bind(this);
      window.addEventListener('scroll', this._onScroll, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
    }

    _handleScroll() {
      if (window.scrollY > this._threshold) {
        this._header.setAttribute('data-scrolled', '');
      } else {
        this._header.removeAttribute('data-scrolled');
      }
    }
  }

  /* ===========================================================================
     MegaMenu — ARIA-enhanced mega menu (CSS does the visual work)
     =========================================================================== */
  class MegaMenu extends HTMLElement {
    connectedCallback() {
      this._navItems = this.querySelectorAll('.header__nav-item');
      this._navItems.forEach(item => {
        const trigger = item.querySelector('.header__nav-link[aria-haspopup]');
        const menu    = item.querySelector('.mega-menu, .header__dropdown');
        if (!trigger || !menu) return;

        trigger.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const isOpen = trigger.getAttribute('aria-expanded') === 'true';
            this._closeAll();
            if (!isOpen) this._open(trigger, menu);
          }
          if (e.key === 'Escape') this._closeAll();
        });

        item.addEventListener('mouseleave', () => this._close(trigger, menu));
      });

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape') this._closeAll();
      });

      document.addEventListener('click', e => {
        if (!this.contains(e.target)) this._closeAll();
      });
    }

    _open(trigger, menu) {
      trigger.setAttribute('aria-expanded', 'true');
      menu.classList.add('is-open');
    }

    _close(trigger, menu) {
      trigger.setAttribute('aria-expanded', 'false');
      menu.classList.remove('is-open');
    }

    _closeAll() {
      this._navItems.forEach(item => {
        const trigger = item.querySelector('[aria-expanded]');
        const menu    = item.querySelector('.mega-menu, .header__dropdown');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (menu)    menu.classList.remove('is-open');
      });
    }
  }

  /* ===========================================================================
     HeaderDrawer — mobile navigation drawer
     =========================================================================== */
  class HeaderDrawer extends HTMLElement {
    connectedCallback() {
      this._drawer   = this.querySelector('.header-drawer');
      this._panel    = this.querySelector('.header-drawer__panel');
      this._backdrop = this.querySelector('.header-drawer__backdrop');
      this._closeBtn = this.querySelector('.header-drawer__close');
      this._openBtn  = document.querySelector('.header__hamburger');
      this._removeTrap = null;

      if (this._openBtn) {
        this._openBtn.addEventListener('click', () => this.open());
      }
      if (this._closeBtn) {
        this._closeBtn.addEventListener('click', () => this.close());
      }
      if (this._backdrop) {
        this._backdrop.addEventListener('click', () => this.close());
      }

      // Submenu toggles
      this.querySelectorAll('.header-drawer__submenu-toggle').forEach(btn => {
        btn.addEventListener('click', () => {
          const submenu = btn.closest('.header-drawer__nav-item').querySelector('.header-drawer__submenu');
          if (submenu) {
            submenu.classList.toggle('is-open');
            btn.setAttribute('aria-expanded', submenu.classList.contains('is-open'));
          }
        });
      });

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this._drawer.classList.contains('is-open')) this.close();
      });
    }

    open() {
      this._drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (this._panel) this._removeTrap = trapFocus(this._panel);
    }

    close() {
      this._drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      if (this._removeTrap) { this._removeTrap(); this._removeTrap = null; }
    }
  }

  /* ===========================================================================
     SearchModal — open/close the search overlay
     =========================================================================== */
  class SearchModal extends HTMLElement {
    connectedCallback() {
      this._modal   = document.getElementById('SearchModal');
      this._openBtns = document.querySelectorAll('[data-search-open]');
      this._closeBtn = this._modal ? this._modal.querySelector('.search-modal__close') : null;
      this._input   = this._modal ? this._modal.querySelector('.search-modal__input') : null;

      this._openBtns.forEach(btn => btn.addEventListener('click', () => this.open()));
      if (this._closeBtn) this._closeBtn.addEventListener('click', () => this.close());

      if (this._modal) {
        this._modal.querySelector('.search-modal__backdrop')?.addEventListener('click', () => this.close());
      }

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this._modal && !this._modal.hasAttribute('hidden')) this.close();
      });
    }

    open() {
      if (!this._modal) return;
      this._modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      setTimeout(() => this._input?.focus(), 50);
    }

    close() {
      if (!this._modal) return;
      this._modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
    }
  }

  /* ===========================================================================
     AnnouncementBar — multi-slide rotator
     =========================================================================== */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this._slides  = this.querySelectorAll('.announcement-bar__slide');
      this._prevBtn = this.querySelector('.announcement-bar__nav-btn--prev');
      this._nextBtn = this.querySelector('.announcement-bar__nav-btn--next');
      if (this._slides.length <= 1) return;

      this._current = 0;
      this._interval = setInterval(() => this._next(), 5000);

      if (this._prevBtn) this._prevBtn.addEventListener('click', () => this._prev());
      if (this._nextBtn) this._nextBtn.addEventListener('click', () => this._next());
    }

    _show(index) {
      this._slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      this._current = index;
    }

    _next() { this._show((this._current + 1) % this._slides.length); }
    _prev() { this._show((this._current - 1 + this._slides.length) % this._slides.length); }

    disconnectedCallback() {
      clearInterval(this._interval);
    }
  }

  /* ===========================================================================
     CartDrawer — slide-out cart
     =========================================================================== */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this._drawer   = this.querySelector('.cart-drawer');
      this._backdrop = this.querySelector('.cart-drawer__backdrop');
      this._closeBtn = this.querySelector('.cart-drawer__close');
      this._itemsEl  = this.querySelector('.cart-drawer__items');
      this._emptyEl  = this.querySelector('.cart-drawer__empty');
      this._footerEl = this.querySelector('.cart-drawer__footer');
      this._countEl  = this.querySelector('.cart-drawer__count');
      this._subtotalEl = this.querySelector('.cart-drawer__subtotal-price');
      this._progressEl = this.querySelector('.cart-drawer__shipping-progress');
      this._shippingTextEl = this.querySelector('.cart-drawer__shipping-text');
      this._threshold = parseFloat(this.dataset.shippingThreshold) || 0;
      this._removeTrap = null;

      if (this._closeBtn) this._closeBtn.addEventListener('click', () => this.close());
      if (this._backdrop) this._backdrop.addEventListener('click', () => this.close());

      PubSub.subscribe(EVENTS.cartDrawerOpen, () => this.open());
      PubSub.subscribe(EVENTS.cartDrawerClose, () => this.close());
      PubSub.subscribe(EVENTS.cartUpdate, (data) => this._refreshCart(data));

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this._drawer && this._drawer.classList.contains('is-open')) this.close();
      });

      // Load current cart state on page load
      this._fetchCart();
    }

    open() {
      if (!this._drawer) return;
      this._drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (this._drawer) this._removeTrap = trapFocus(this._drawer);
    }

    close() {
      if (!this._drawer) return;
      this._drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      if (this._removeTrap) { this._removeTrap(); this._removeTrap = null; }
    }

    _fetchCart() {
      fetchJSON('/cart.js').then(cart => this._renderCart(cart)).catch(() => {});
    }

    _refreshCart(data) {
      if (data && data.cart) {
        this._renderCart(data.cart);
      } else {
        this._fetchCart();
      }
    }

    _renderCart(cart) {
      // Update header cart count
      document.querySelectorAll('.cart-count-bubble').forEach(el => {
        el.textContent = cart.item_count;
        el.dataset.count = cart.item_count;
      });

      if (!this._itemsEl) return;

      if (cart.item_count === 0) {
        if (this._itemsEl)  this._itemsEl.innerHTML = '';
        if (this._emptyEl)  this._emptyEl.hidden = false;
        if (this._footerEl) this._footerEl.hidden = true;
        if (this._countEl)  this._countEl.textContent = '';
        return;
      }

      if (this._emptyEl)  this._emptyEl.hidden = true;
      if (this._footerEl) this._footerEl.hidden = false;

      if (this._countEl) {
        this._countEl.textContent = `(${cart.item_count})`;
      }

      if (this._subtotalEl) {
        this._subtotalEl.textContent = moneyFormat(cart.total_price);
      }

      this._renderShippingBar(cart.total_price);

      this._itemsEl.innerHTML = cart.items.map(item => this._renderItem(item)).join('');

      // Wire up qty and remove buttons
      this._itemsEl.querySelectorAll('[data-qty-change]').forEach(btn => {
        btn.addEventListener('click', () => {
          const key   = btn.dataset.key;
          const delta = parseInt(btn.dataset.qtyChange);
          const input = btn.closest('.cart-item__qty').querySelector('.cart-item__qty-input');
          const newQty = Math.max(0, parseInt(input.value) + delta);
          this._updateItem(key, newQty);
        });
      });

      this._itemsEl.querySelectorAll('[data-remove-item]').forEach(btn => {
        btn.addEventListener('click', () => this._updateItem(btn.dataset.removeItem, 0));
      });
    }

    _renderItem(item) {
      const img = item.image
        ? `<img src="${item.image.replace('.jpg', '_80x80.jpg')}" alt="${item.title}" class="cart-item__img" width="80" height="80" loading="lazy">`
        : `<div class="cart-item__img" style="background:#1a1a1a;"></div>`;

      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<div class="cart-item__variant">${item.variant_title}</div>` : '';

      return `
        <div class="cart-item" data-key="${item.key}">
          <div class="cart-item__img-wrap">${img}</div>
          <div class="cart-item__details">
            ${item.vendor ? `<div class="cart-item__vendor">${item.vendor}</div>` : ''}
            <div class="cart-item__title">${item.product_title}</div>
            ${variant}
            <div class="cart-item__footer">
              <div class="cart-item__qty">
                <button class="cart-item__qty-btn" data-qty-change="-1" data-key="${item.key}" aria-label="Decrease quantity">−</button>
                <input class="cart-item__qty-input" type="number" value="${item.quantity}" min="0" readonly>
                <button class="cart-item__qty-btn" data-qty-change="1" data-key="${item.key}" aria-label="Increase quantity">+</button>
              </div>
              <div class="cart-item__price">${moneyFormat(item.line_price)}</div>
            </div>
          </div>
          <button class="cart-item__remove icon-btn" data-remove-item="${item.key}" aria-label="Remove ${item.product_title}">
            <svg class="icon icon--sm" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      `;
    }

    _renderShippingBar(totalPrice) {
      if (!this._threshold || !this._progressEl) return;
      const thresholdCents = this._threshold * 100;
      const percent = Math.min(100, (totalPrice / thresholdCents) * 100);
      this._progressEl.style.width = percent + '%';

      if (this._shippingTextEl) {
        if (percent >= 100) {
          this._shippingTextEl.textContent = "You've unlocked free shipping!";
          this._progressEl.style.background = 'var(--color-success)';
        } else {
          const remaining = moneyFormat(thresholdCents - totalPrice);
          this._shippingTextEl.textContent = `${remaining} away from free shipping`;
        }
      }
    }

    _updateItem(key, qty) {
      fetchJSON('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: qty })
      }).then(cart => {
        PubSub.publish(EVENTS.cartUpdate, { cart });
      }).catch(() => showNotification('Could not update cart', 'error'));
    }
  }

  /* ===========================================================================
     ProductForm — AJAX add to cart
     =========================================================================== */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this._form    = this.querySelector('form');
      this._btn     = this.querySelector('[data-atc-btn]');
      this._origTxt = this._btn ? this._btn.textContent : '';

      if (!this._form) return;
      this._form.addEventListener('submit', e => {
        e.preventDefault();
        this._submit();
      });
    }

    _submit() {
      if (!this._btn) return;
      this._btn.disabled  = true;
      this._btn.textContent = 'Adding...';
      this._btn.classList.add('loading');

      const formData = new FormData(this._form);
      formData.append('sections', 'cart-drawer');

      fetch('/cart/add.js', { method: 'POST', body: formData })
        .then(r => r.json())
        .then(data => {
          if (data.status) throw new Error(data.description || 'Could not add to cart');
          return fetchJSON('/cart.js');
        })
        .then(cart => {
          PubSub.publish(EVENTS.cartUpdate, { cart });
          if (document.body.dataset.cartType === 'drawer') {
            PubSub.publish(EVENTS.cartDrawerOpen);
          }
          showNotification('Added to cart!', 'success');
        })
        .catch(err => {
          showNotification(err.message || 'Could not add to cart', 'error');
        })
        .finally(() => {
          this._btn.disabled  = false;
          this._btn.textContent = this._origTxt;
          this._btn.classList.remove('loading');
        });
    }
  }

  /* ===========================================================================
     VariantSelects — native <select> variant switching
     =========================================================================== */
  class VariantSelects extends HTMLElement {
    connectedCallback() {
      this._productData = this._getProductData();
      this._selects     = this.querySelectorAll('select');
      this._selects.forEach(sel => sel.addEventListener('change', () => this._onChange()));
    }

    _getProductData() {
      const el = this.closest('[data-product]') || document;
      const jsonEl = el.querySelector('[data-product-json]');
      if (!jsonEl) return null;
      try { return JSON.parse(jsonEl.textContent); } catch { return null; }
    }

    _onChange() {
      const selectedOptions = Array.from(this._selects).map(s => s.value);
      const variant = this._findVariant(selectedOptions);
      if (!variant) return;

      PubSub.publish(EVENTS.variantChange, { variant });
      this._updatePrice(variant);
      this._updateATC(variant);
      this._updateURL(variant);
      this._updateGallery(variant);
    }

    _findVariant(options) {
      if (!this._productData) return null;
      return this._productData.variants.find(v =>
        v.options.every((o, i) => o === options[i])
      );
    }

    _updatePrice(variant) {
      const priceEl = document.querySelector('[data-product-price]');
      if (!priceEl) return;
      const regular  = priceEl.querySelector('.price__regular');
      const compare  = priceEl.querySelector('.price__compare');
      if (regular) regular.textContent = moneyFormat(variant.price);
      if (compare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compare.textContent = moneyFormat(variant.compare_at_price);
          compare.hidden = false;
        } else {
          compare.hidden = true;
        }
      }
    }

    _updateATC(variant) {
      const btn = document.querySelector('[data-atc-btn]');
      if (!btn) return;
      if (variant.available) {
        btn.disabled = false;
        btn.textContent = 'Add to Cart';
      } else {
        btn.disabled = true;
        btn.textContent = 'Sold Out';
      }
    }

    _updateURL(variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    _updateGallery(variant) {
      if (!variant.featured_image) return;
      const gallery = document.querySelector('product-media-gallery');
      if (gallery) gallery.setActiveImage(variant.featured_image.position - 1);
    }
  }

  /* ===========================================================================
     ProductMediaGallery — thumbnail navigation
     =========================================================================== */
  class ProductMediaGallery extends HTMLElement {
    connectedCallback() {
      this._featured = this.querySelector('.product-gallery__featured-img');
      this._thumbs   = this.querySelectorAll('.product-gallery__thumb');

      this._thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.setActiveImage(i));
        thumb.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.setActiveImage(i); }
        });
      });
    }

    setActiveImage(index) {
      const thumb = this._thumbs[index];
      if (!thumb) return;

      this._thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');

      const img = thumb.querySelector('img');
      if (img && this._featured) {
        this._featured.src    = img.dataset.full || img.src.replace('_72x72', '_800x');
        this._featured.srcset = img.dataset.srcset || '';
        this._featured.alt    = img.alt;
      }
    }
  }

  /* ===========================================================================
     ProductTabs — tabbed panel switching
     =========================================================================== */
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this._tabs   = this.querySelectorAll('.product-tabs__tab');
      this._panels = this.querySelectorAll('.product-tabs__panel');

      this._tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this._activate(i));
      });
    }

    _activate(index) {
      this._tabs.forEach((t, i)   => t.classList.toggle('is-active', i === index));
      this._panels.forEach((p, i) => p.classList.toggle('is-active', i === index));
    }
  }

  /* ===========================================================================
     FacetFiltersForm — AJAX collection filtering
     =========================================================================== */
  class FacetFiltersForm extends HTMLElement {
    connectedCallback() {
      this._form       = this.querySelector('form');
      this._productGrid = document.getElementById('ProductGrid');
      this._loading    = false;

      if (!this._form) return;

      this._form.addEventListener('change', debounce(() => this._submit(), 300));

      // Sort by
      const sortBy = document.querySelector('[data-sort-by]');
      if (sortBy) sortBy.addEventListener('change', debounce(() => this._submit(), 100));

      // View toggle
      document.querySelectorAll('.view-toggle__btn').forEach(btn => {
        btn.addEventListener('click', () => {
          document.querySelectorAll('.view-toggle__btn').forEach(b => b.classList.toggle('is-active', b === btn));
          const grid = document.getElementById('ProductGrid');
          if (grid) grid.classList.toggle('product-grid--list', btn.dataset.view === 'list');
        });
      });

      // Active facet removal
      this._bindActiveFacets();

      // Mobile filters drawer
      const filterBtn  = document.querySelector('[data-filters-drawer-open]');
      const filterClose = document.querySelectorAll('[data-filters-drawer-close]');
      const filterDrawer = document.getElementById('FiltersDrawer');
      const applyBtn   = document.querySelector('[data-filters-apply]');

      if (filterBtn && filterDrawer) {
        filterBtn.addEventListener('click', () => {
          filterDrawer.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        });
      }
      filterClose.forEach(btn => {
        btn.addEventListener('click', () => {
          filterDrawer?.classList.remove('is-open');
          document.body.style.overflow = '';
        });
      });
      filterDrawer?.querySelector('.filters-drawer__backdrop')?.addEventListener('click', () => {
        filterDrawer.classList.remove('is-open');
        document.body.style.overflow = '';
      });
      if (applyBtn) applyBtn.addEventListener('click', () => {
        filterDrawer?.classList.remove('is-open');
        document.body.style.overflow = '';
        this._submit();
      });
    }

    _bindActiveFacets() {
      document.querySelectorAll('[data-active-facet]').forEach(el => {
        el.addEventListener('click', e => {
          e.preventDefault();
          window.history.pushState({}, '', el.href);
          this._fetchSection(el.href);
        });
      });

      const clearAll = document.querySelector('[data-clear-all-facets]');
      if (clearAll) {
        clearAll.addEventListener('click', e => {
          e.preventDefault();
          window.history.pushState({}, '', clearAll.href);
          this._fetchSection(clearAll.href);
        });
      }
    }

    _submit() {
      if (this._loading) return;
      const sortSelect = document.querySelector('[data-sort-by]');
      const params     = new URLSearchParams(new FormData(this._form));
      if (sortSelect) params.set('sort_by', sortSelect.value);

      const url = `${window.location.pathname}?${params.toString()}`;
      window.history.pushState({}, '', url);
      this._fetchSection(url);
    }

    _fetchSection(url) {
      this._loading = true;
      if (this._productGrid) this._productGrid.style.opacity = '0.4';

      const sectionUrl = `${url}${url.includes('?') ? '&' : '?'}section_id=main-collection`;

      fetch(sectionUrl)
        .then(r => r.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');

          const newGrid = doc.getElementById('ProductGrid');
          if (newGrid && this._productGrid) {
            this._productGrid.innerHTML = newGrid.innerHTML;
          }

          const newForm = doc.querySelector('#FacetFiltersForm');
          if (newForm && this._form) {
            this._form.innerHTML = newForm.innerHTML;
          }

          const newActiveFacets = doc.querySelector('[data-active-facets-container]');
          const curActiveFacets = document.querySelector('[data-active-facets-container]');
          if (newActiveFacets && curActiveFacets) {
            curActiveFacets.innerHTML = newActiveFacets.innerHTML;
          }

          const newCount = doc.querySelector('[data-collection-count]');
          const curCount = document.querySelector('[data-collection-count]');
          if (newCount && curCount) curCount.innerHTML = newCount.innerHTML;

          this._bindActiveFacets();

          if (this._productGrid) this._productGrid.style.opacity = '1';
          this._productGrid?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        })
        .catch(() => {
          if (this._productGrid) this._productGrid.style.opacity = '1';
        })
        .finally(() => { this._loading = false; });
    }
  }

  /* ===========================================================================
     PriceRange — dual input price range filter
     =========================================================================== */
  class PriceRange extends HTMLElement {
    connectedCallback() {
      this._minInput = this.querySelector('[data-price-min]');
      this._maxInput = this.querySelector('[data-price-max]');

      if (!this._minInput || !this._maxInput) return;

      const onChange = debounce(() => {
        const form = this.closest('form');
        if (form) form.dispatchEvent(new Event('change', { bubbles: true }));
      }, 600);

      this._minInput.addEventListener('change', onChange);
      this._maxInput.addEventListener('change', onChange);
    }
  }

  /* ===========================================================================
     PredictiveSearch — live search suggestions
     =========================================================================== */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this._input   = this.querySelector('input[type="search"]');
      this._results = this.querySelector('#predictive-search-results');
      if (!this._input || !this._results) return;

      this._search = debounce(() => this._fetch(), 300);
      this._input.addEventListener('input', this._search);

      document.addEventListener('click', e => {
        if (!this.contains(e.target)) this._results.innerHTML = '';
      });
    }

    _fetch() {
      const query = this._input.value.trim();
      if (query.length < 2) { this._results.innerHTML = ''; return; }

      const types = (this.dataset.resourceTypes || 'product').split(',').join('&resources[type][]=');
      const url   = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type][]=${types}&resources[limit]=6`;

      fetchJSON(url).then(data => {
        this._render(data.resources.results);
      }).catch(() => {});
    }

    _render(results) {
      const products = results.products || [];
      if (!products.length) { this._results.innerHTML = `<p style="padding:16px;color:#666;font-size:.875rem;">No results found.</p>`; return; }

      const html = products.map(p => `
        <a href="${p.url}" class="predictive-search__item">
          <img class="predictive-search__item-img" src="${p.image || ''}" alt="${p.title}" width="48" height="48" loading="lazy">
          <div>
            <div class="predictive-search__item-title">${p.title}</div>
            <div class="predictive-search__item-price">${moneyFormat(p.price * 100)}</div>
          </div>
        </a>
      `).join('');

      this._results.innerHTML = html + `
        <a href="/search?q=${encodeURIComponent(this._input.value)}&type=product" class="predictive-search__view-all">
          View all results →
        </a>
      `;
    }
  }

  /* ===========================================================================
     QuantityInput — +/- stepper on PDP
     =========================================================================== */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this._input  = this.querySelector('.quantity-input__field');
      this._minBtn = this.querySelector('[data-qty-minus]');
      this._plusBtn = this.querySelector('[data-qty-plus]');

      if (this._minBtn) this._minBtn.addEventListener('click', () => this._change(-1));
      if (this._plusBtn) this._plusBtn.addEventListener('click', () => this._change(1));
    }

    _change(delta) {
      if (!this._input) return;
      const min = parseInt(this._input.min) || 1;
      const max = parseInt(this._input.max) || 999;
      const val = parseInt(this._input.value) || 1;
      this._input.value = Math.min(max, Math.max(min, val + delta));
    }
  }

  /* ===========================================================================
     ProductRecommendations — fetch and render product recommendations
     =========================================================================== */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const productId = this.dataset.productId;
      const limit     = this.dataset.limit || 4;
      const sectionId = this.dataset.sectionId || 'product-recommendations';

      if (!productId) return;

      fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}&section_id=${sectionId}`)
        .then(r => r.json())
        .then(data => {
          if (data.products && data.products.length) {
            this.removeAttribute('hidden');
          }
        })
        .catch(() => {});
    }
  }

  /* ===========================================================================
     Custom Element Registration
     =========================================================================== */
  customElements.define('sticky-header',            StickyHeader);
  customElements.define('mega-menu',                MegaMenu);
  customElements.define('header-drawer',            HeaderDrawer);
  customElements.define('search-modal',             SearchModal);
  customElements.define('announcement-bar',         AnnouncementBar);
  customElements.define('cart-drawer',              CartDrawer);
  customElements.define('product-form',             ProductForm);
  customElements.define('variant-selects',          VariantSelects);
  customElements.define('product-media-gallery',    ProductMediaGallery);
  customElements.define('product-tabs',             ProductTabs);
  customElements.define('facet-filters-form',       FacetFiltersForm);
  customElements.define('price-range',              PriceRange);
  customElements.define('predictive-search',        PredictiveSearch);
  customElements.define('quantity-input',           QuantityInput);
  customElements.define('product-recommendations',  ProductRecommendations);

})();
